import {type Directive, type DirectiveBinding} from 'vue';

let animationEnabled                 = true;
let processedElements: HTMLElement[] = [];

function create(element: HTMLElement, directiveOptions: DirectiveBinding)
{
    animationEnabled = !document.querySelector('#page.disable-animation');

    if (animationEnabled)
    {
        element.style.overflow = 'hidden';

        element.addEventListener('transitionend', event =>
        {
            // re-enable natural height behaviour, if opened after transition
            if (element.clientHeight && event.target === element)
            {
                element.style.removeProperty('height');
            }
        });
    }

    if (!directiveOptions.value)
    {
        element.style.overflow = 'hidden';
        element.style.height   = 0 + 'px';
    }

    setLastToggleValue(element, directiveOptions.value);
}

function update(element: HTMLElement, directiveOptions: DirectiveBinding)
{
    if (getLastToggleValue(element) === directiveOptions.value)
    {
        return;
    }

    setLastToggleValue(element, directiveOptions.value);

    if (animationEnabled)
    {
        let arg                  = directiveOptions.arg as number | undefined;
        element.style.transition = 'height ' + (arg || arg === 0 ? arg : .5) + 's';
    }

    removeFromProcessedElements(element);

    function enableNaturalHeight()
    {
        // re-enable natural height behaviour
        element.style.removeProperty('height');
    }

    function getElementHeight()
    {
        return element.scrollHeight || element.clientHeight || element.offsetHeight;
    }

    function setElementHeight(height: number)
    {
        element.style.height = height + 'px';
    }

    // open
    if (directiveOptions.value)
    {
        if (animationEnabled)
        {
            if (getElementHeight())
            {
                setElementHeight(getElementHeight());
            }
            // retry after 100ms
            else
            {
                window.setTimeout(() =>
                {
                    if (getElementHeight())
                    {
                        setElementHeight(getElementHeight());
                    }
                    // enable natural height if still no height available
                    else
                    {
                        enableNaturalHeight();
                    }
                }, 100);
            }
        }
        else
        {
            enableNaturalHeight();
        }
    }

    // close
    else
    {
        // set current height and 0 afterwards with a slight delay for the transition to work
        setElementHeight(element.clientHeight);

        processedElements.push(element);

        window.setTimeout(() =>
        {
            // only set to 0 if value hasn't been changed within the timeout
            if (processedElements.indexOf(element) !== -1)
            {
                setElementHeight(0);
                removeFromProcessedElements(element);
            }
        }, 50);
    }
}

function removeFromProcessedElements(element: HTMLElement)
{
    const index = processedElements.indexOf(element);

    if (index !== -1)
    {
        processedElements.splice(index, 1);
    }
}

function setLastToggleValue(element: HTMLElement, value: boolean): void
{
    element.dataset.lastToggleValue = value ? '1' : '0';
}

function getLastToggleValue(element: HTMLElement): boolean
{
    return element.dataset.lastToggleValue === '1';
}

const toggle: Directive = {
    mounted: create,
    updated: update,
};

export default toggle;